import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import { getSubjects, globalConst, isEmptyArr } from 'modules/global';
import { getTopicTests, TopicTestLayout, topicTestsConst, useTopicTests } from 'modules/mavzuli-test';

import { CustomPagination, EmptyData, LimitFinishedModal, NextHead, SearchInput, TestCard, Visible } from 'components';

function Index() {
  const t = useTranslations();
  const { locale = 'uz', query } = useRouter();

  const [queryParams] = useQueryParams({
    search: withDefault(StringParam, ''),
    page: withDefault(StringParam, '1')
  });

  const selectedSubject = query?.subject !== t('barchasi') ? query?.subject : undefined;

  const { data, isLoading, refetch } = useTopicTests({
    search: queryParams?.search || undefined,
    page: queryParams.page as string,
    languageId: globalConst.langIds[locale],
    name: selectedSubject as string
  });

  return (
    <>
      <NextHead title={t('meta.topic_test.title')} description={t('meta.topic_test.description')} />
      <TopicTestLayout>
        <div className="shadow-xs mb-4 hidden items-center justify-between rounded-xl bg-white px-4 py-3 lg:flex">
          <div className="flex items-center gap-2 text-lg font-medium">{t('barchasi')}</div>
          <SearchInput refetch={refetch} loading={isLoading} />
        </div>

        <Visible
          if={isLoading}
          else={
            <Visible
              if={isEmptyArr(data?.data)}
              else={
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
                  {data?.data?.map(savedTest => (
                    <React.Fragment key={savedTest?.test_id}>
                      <TestCard
                        key={savedTest?.test_id}
                        isNew={savedTest?.is_new}
                        testId={savedTest?.test_id}
                        teacher={savedTest?.teacher}
                        isSaved={savedTest?.is_saved}
                        testUrl={savedTest?.test_url}
                        viewCount={savedTest?.view_count}
                        testCount={savedTest?.test_count}
                        testRating={savedTest?.test_rating}
                        lessonName={savedTest?.lesson_name}
                        subjectName={savedTest?.subject_name}
                        learningCenter={savedTest?.learning_center}
                      />

                      {/* Yana banner qo'yilishini hisobga olib o'chirilmadi
                                    
                                    <Visible if={(index === 1 && isMobile) || (index === 5 && !isMobile)}>
                                      <div className="sm:h-50 h-26 relative col-span-1 w-full rounded-xl sm:col-span-2 md:col-span-3 lg:col-span-3">
                                        {!isLoaded && (
                                          <div className="absolute inset-0 z-0 h-full w-full animate-pulse rounded-xl bg-gray-200" />
                                        )}
                                        <Image
                                          fill
                                          alt="Akam reklama banner"
                                          src="/images/themed-advertisement.webp"
                                          onLoadingComplete={() => setIsLoaded(true)}
                                          title="Test yeching va o'quv markazlarga chegirma qo'lga kiriting"
                                          className={classNames('rounded-xl', {
                                            visible: isLoaded,
                                            invisible: !isLoaded
                                          })}
                                        />
                                      </div>
                                    </Visible> */}
                    </React.Fragment>
                  ))}
                </div>
              }
            >
              <EmptyData title={t('common.empty_test_data')} />
            </Visible>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 10 }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton.Button key={index} active className="!h-40 !w-full !rounded-xl" />
            ))}
          </div>
        </Visible>
        {data && (
          <div className="mt-5">
            <CustomPagination total={data?.total} />
          </div>
        )}
      </TopicTestLayout>

      <LimitFinishedModal />
    </>
  );
}

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const queryClient = new QueryClient();
  const languageId = globalConst.langIds[locale || 'uz'];

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [topicTestsConst.queryKeys.USE_TOPIC_TEST, '1', languageId, null, null],
      queryFn: getTopicTests({ page: '1', languageId })
    }),
    queryClient.prefetchQuery({
      queryKey: [globalConst.queryKeys.SUBJECTS_DATA],
      queryFn: getSubjects
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};
